import { useTranslations } from '@vocab/react';
import { Button, IconAI } from 'braid-design-system';
import { useCallback, useEffect } from 'react';

import { useSignedInDashboardContext } from '../../SignedInDashboardContextProvider';
import { CareerFeedAiAssistantInitialState } from '../CareerFeedAiAssistantInitialState/CareerFeedAiAssistantInitialState';
import { CareerFeedAssistantQuestions } from '../CareerFeedAssistantQuestions/CareerFeedAssistantQuestions';
import {
  useGetRefreshRecommendationsWithCandidateChatContext,
  useGetResetCandidateChatContext,
} from '../hooks/candidateChatContextHelpers';
import { useCareerFeedAIAssistant } from '../hooks/useCareerFeedAIAssistant';

import translations from './.vocab';
import { CareerFeedAIAssistantContentFrame } from './CareerFeedAIAssistantContentFrame';
import { TextSection } from './TextSection';

const ConversationFinished = () => {
  const { t } = useTranslations(translations);

  const {
    lastCompletedAssistantResponse,
    tracking: { trackRecommendedJobsRefreshed, trackGenAIChatEnded },
  } = useCareerFeedAIAssistant();
  const {
    recommendationsResult: { isRefreshLoading },
  } = useSignedInDashboardContext();

  const refreshRecommendations =
    useGetRefreshRecommendationsWithCandidateChatContext();

  const onRefresh = useCallback(async () => {
    const result = await refreshRecommendations();

    if (result.success) {
      trackGenAIChatEnded();
      trackRecommendedJobsRefreshed({ jobsCount: result.jobsCount });
    } else {
      trackGenAIChatEnded(Boolean(result.error));
      trackRecommendedJobsRefreshed({ jobsCount: 0 });
    }
  }, [
    refreshRecommendations,
    trackGenAIChatEnded,
    trackRecommendedJobsRefreshed,
  ]);

  return (
    <CareerFeedAIAssistantContentFrame
      headMessage={lastCompletedAssistantResponse!.text}
      contentSection={
        <TextSection>{t('These AI-powered job suggestions....')}</TextSection>
      }
      ctaSection={
        <Button
          tone="brandAccent"
          icon={<IconAI />}
          onClick={onRefresh}
          loading={isRefreshLoading}
        >
          {t('Explore recommendations')}
        </Button>
      }
    />
  );
};

const RecommendationRefinedConfirmation = () => {
  const resetPreferences = useGetResetCandidateChatContext();
  const { t } = useTranslations(translations);

  return (
    <CareerFeedAIAssistantContentFrame
      headMessage={t(
        "We've updated your recommendations based on your answers. Take a look at the personalised job listings.",
      )}
      ctaSection={<Button onClick={resetPreferences}>{t('Reset')}</Button>}
    />
  );
};

export const CareerFeedAIAssistantContent = () => {
  const {
    sequence,
    progress,
    tracking: {
      trackGenAIChatOutputDisplayed,
      trackGenAIChatStarted,
      trackGenAIChatCtaDisplayed,
    },
  } = useCareerFeedAIAssistant();

  useEffect(() => {
    if (progress === 'initial') {
      trackGenAIChatCtaDisplayed();
    } else if (
      progress !== 'recsRefined' &&
      progress !== 'recsRefined-withPersistedData'
    ) {
      trackGenAIChatOutputDisplayed();
    }

    if (sequence === 1) {
      trackGenAIChatStarted();
    }
  }, [
    progress,
    sequence,
    trackGenAIChatCtaDisplayed,
    trackGenAIChatOutputDisplayed,
    trackGenAIChatStarted,
  ]);

  switch (progress) {
    case 'initial':
      return <CareerFeedAiAssistantInitialState />;
    case 'recsRefined':
    case 'recsRefined-withPersistedData':
      return <RecommendationRefinedConfirmation />;
    case 'reviewRefinement':
      return <ConversationFinished />;
    case 'gatheringRefinement':
    case 'collectGeneralRequirementsAtLast':
      return <CareerFeedAssistantQuestions />;
  }
};
